import React, { useState, useEffect, useCallback } from 'react';
import { Card, Tabs, Typography, Button, Modal, Space, Table, Tag, Empty } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import ConfigurableLocationSelector from '@/modules/location/components/ConfigurableLocationSelector';
import useCustomerLocation from '@/hooks/admin/useCustomerLocation';
import { useLocationConfig } from '@/modules/location/hooks/useLocationConfig';
import { CustomerLocationDTO, LocationConfigDTO, CustomerProfileDTO } from '@/shared/api/model';
import { ColumnsType } from "antd/es/table";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface LocationFormData {
  locationSelections: {
    zone?: any;
    region?: any;
    district?: any;
    ward?: any;
    street?: any;
    fullPath?: string;
  };
  gpsCoordinates: {
    latitude?: number;
    longitude?: number;
    accuracy?: number;
  };
  houseNumber: string;
  postalCode: string;
  addressType: string;
  isPrimary: boolean;
  validationErrors?: string[];
}

interface CustomerLocationManagementProps {
  customerProfile: CustomerProfileDTO | null;
}

export const CustomerLocationManagement: React.FC<CustomerLocationManagementProps> = ({ customerProfile }) => {
  const { dark } = useTheme();
  const [activeTab, setActiveTab] = useState("locations");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentAddressType, setCurrentAddressType] = useState("");
  const [configMap, setConfigMap] = useState<Record<string, LocationConfigDTO>>({});
  const [formData, setFormData] = useState<LocationFormData>({
    locationSelections: {},
    gpsCoordinates: {},
    houseNumber: "",
    postalCode: "",
    addressType: "",
    isPrimary: false
  });
  const [validationResult, setValidationResult] = useState<{valid: boolean, errors: string[]} | null>(null);

  const { fetchConfigsByEntityType, configs } = useLocationConfig();

  const {
    useCustomerLocations,
    useSaveCustomerLocation,
    useDeleteCustomerLocation
  } = useCustomerLocation();

  const { data: customerLocationsData, isLoading: isLoadingLocations } = useCustomerLocations({
    enabled: !!customerProfile?.branch
  });
  const [customerLocations, setCustomerLocations] = useState<CustomerLocationDTO[]>([]);
  const { mutate: saveLocation, isPending: isSaving } = useSaveCustomerLocation();
  const { mutate: deleteLocation } = useDeleteCustomerLocation();

  useEffect(() => {
    fetchConfigsByEntityType('CUSTOMER', false);
  }, [fetchConfigsByEntityType]);

  useEffect(() => {
    if (configs) {
      const configsByType: Record<string, LocationConfigDTO> = {};
      configs.forEach(config => {
        if (config.entityType === 'CUSTOMER' && config.isActive) {
          // @ts-ignore
          configsByType[config.addressType] = config;
        }
      });
      setConfigMap(configsByType);
    }
  }, [configs]);

  useEffect(() => {
    if (customerLocationsData) {
      const locationsData = Array.isArray(customerLocationsData)
          ? customerLocationsData as CustomerLocationDTO[]
          : [];
      setCustomerLocations(locationsData);
    }
  }, [customerLocationsData]);

  // Handle data from ConfigurableLocationSelector
  const handleLocationDataChange = useCallback((locationData: any) => {
    console.log('CustomerLocationManagement - handleLocationDataChange received:', locationData);

    // Update form data
    setFormData(prev => ({
      ...prev,
      ...locationData
    }));

    // Update validation result if provided
    if (locationData.validationErrors !== undefined) {
      console.log('CustomerLocationManagement - validation errors received:', locationData.validationErrors);

      setValidationResult({
        valid: locationData.validationErrors.length === 0,
        errors: locationData.validationErrors
      });
    }
  }, []);

  const handleAddLocation = useCallback((addressType: string) => {
    setCurrentAddressType(addressType);
    setFormData({
      locationSelections: {},
      gpsCoordinates: {},
      houseNumber: "",
      postalCode: "",
      addressType: addressType,
      isPrimary: false
    });
    setValidationResult(null);
    setIsModalVisible(true);
  }, []);

  const handleEditLocation = useCallback((location: CustomerLocationDTO) => {
    if (location.addressType) {
      setCurrentAddressType(location.addressType);
    } else {
      setCurrentAddressType("");
    }

    setFormData({
      locationSelections: {
        zone: location.zone,
        region: location.region,
        district: location.district,
        ward: location.ward,
        street: location.street
      },
      gpsCoordinates: {
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.gpsAccuracy
      },
      houseNumber: location.houseNumber || "",
      postalCode: location.postalCode || "",
      addressType: location.addressType || "",
      isPrimary: location.isPrimary || false
    });

    setValidationResult(null);
    setIsModalVisible(true);
  }, []);

  const handleDeleteLocation = useCallback((addressType: string) => {
    Modal.confirm({
      title: 'Delete Location',
      content: 'Are you sure you want to delete this location?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => {
        deleteLocation({ addressType });
      }
    });
  }, [deleteLocation]);

  const handleSaveLocation = useCallback(async () => {
    console.log('CustomerLocationManagement - handleSaveLocation called');
    console.log('Current form data:', formData);
    console.log('Current validation result:', validationResult);

    try {
      // Check if there are validation errors
      if (validationResult && !validationResult.valid) {
        console.log('Validation errors exist, cannot save:', validationResult.errors);
        return;
      }

      // Proceed with save if validation is successful
      const locationData: CustomerLocationDTO = {
        addressType: formData.addressType,
        zone: formData.locationSelections.zone,
        region: formData.locationSelections.region,
        district: formData.locationSelections.district,
        ward: formData.locationSelections.ward,
        street: formData.locationSelections.street,
        houseNumber: formData.houseNumber,
        postalCode: formData.postalCode,
        latitude: formData.gpsCoordinates?.latitude,
        longitude: formData.gpsCoordinates?.longitude,
        gpsAccuracy: formData.gpsCoordinates?.accuracy,
        isPrimary: formData.isPrimary
      };

      console.log('Saving location data:', locationData);
      saveLocation({ data: locationData });
      setIsModalVisible(false);
    } catch (error) {
      console.error('Save failed:', error);
    }
  }, [formData, saveLocation, validationResult]);

  const handleCancel = useCallback(() => {
    setIsModalVisible(false);
    setFormData({
      locationSelections: {},
      gpsCoordinates: {},
      houseNumber: "",
      postalCode: "",
      addressType: "",
      isPrimary: false
    });
    setValidationResult(null);
  }, []);

  // Render the location form using the ConfigurableLocationSelector
  const renderLocationForm = useCallback(() => {
    return (
        <Modal
            title={`${currentAddressType} Location`}
            open={isModalVisible}
            onOk={handleSaveLocation}
            onCancel={handleCancel}
            confirmLoading={isSaving}
            width="95%"
            style={{ maxWidth: '700px', top: 20 }}
            centered
            okButtonProps={{
              disabled: validationResult ? !validationResult.valid : false
            }}
        >
          <div className="space-y-4">
            {currentAddressType && (
                <ConfigurableLocationSelector
                    entityType="CUSTOMER"
                    addressType={currentAddressType}
                    initialValues={{
                      locationSelections: formData.locationSelections,
                      gpsCoordinates: formData.gpsCoordinates,
                      houseNumber: formData.houseNumber,
                      postalCode: formData.postalCode,
                      isPrimary: formData.isPrimary
                    }}
                    onLocationSelected={handleLocationDataChange}
                    disabled={false}
                />
            )}

            {validationResult && !validationResult.valid && (
                <Card title="Validation Errors" className="bg-red-50">
                  <ul className="text-red-600 list-disc pl-4">
                    {validationResult.errors.map((error: string, index: number) => (
                        <li key={index}>{error}</li>
                    ))}
                  </ul>
                </Card>
            )}
          </div>
        </Modal>
    );
  }, [currentAddressType, formData, validationResult, handleLocationDataChange, isSaving]);

  const columns: ColumnsType<CustomerLocationDTO> = [
    {
      title: 'Type',
      dataIndex: 'addressType',
      key: 'addressType',
      width: 100,
      fixed: 'left',
      render: (text: string) => <span style={{ fontSize: '12px' }}>{text}</span>
    },
    {
      title: 'Location Path',
      key: 'path',
      ellipsis: true,
      render: (_: any, record: CustomerLocationDTO) => {
        const parts = [];
        if (record.zone) parts.push(record.zone.name);
        if (record.region) parts.push(record.region.name);
        if (record.district) parts.push(record.district.name);
        if (record.ward) parts.push(record.ward.name);
        if (record.street) parts.push(record.street.name);

        return <Text style={{ fontSize: '12px' }}>{parts.join(' > ')}</Text>;
      }
    },
    {
      title: 'House',
      dataIndex: 'houseNumber',
      key: 'houseNumber',
      width: 80,
      render: (text: string) => <span style={{ fontSize: '12px' }}>{text}</span>
    },
    {
      title: 'Status',
      key: 'status',
      width: 90,
      render: (_: any, record: CustomerLocationDTO) => (
          record.isPrimary ? <Tag color="green" style={{ fontSize: '11px' }}>Primary</Tag> : <Tag color="default" style={{ fontSize: '11px' }}>Secondary</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_: any, record: CustomerLocationDTO) => (
          <Space size="small">
            <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => handleEditLocation(record)}
            />
            <Button
                icon={<DeleteOutlined />}
                size="small"
                danger
                // @ts-ignore
                onClick={() => handleDeleteLocation(record.addressType)}
            />
          </Space>
      )
    }
  ];

  return (
      <Card
          className={dark ? 'bg-gray-800 text-white' : ''}
          title={
            <Title level={4} className={dark ? 'text-white mb-0' : 'mb-0'}>
              <EnvironmentOutlined /> Location Management
            </Title>
          }
          extra={
            <Space wrap style={{ maxWidth: '100%' }}>
              {Object.entries(configMap).map(([addressType, config]) => (
                  <Button
                      key={addressType}
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => handleAddLocation(addressType)}
                      size="small"
                      style={{ fontSize: '12px' }}
                  >
                    <span className="hidden sm:inline">Add </span>{addressType}
                  </Button>
              ))}
            </Space>
          }
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Customer Locations" key="locations">
            {isLoadingLocations ? (
                <div className="text-center p-8">Loading locations...</div>
            ) : Array.isArray(customerLocations) && customerLocations.length > 0 ? (
                <Table
                    dataSource={customerLocations as CustomerLocationDTO[]}
                    columns={columns}
                    rowKey={(record) => `${record.addressType || 'unknown'}_${Math.random()}`}
                    pagination={{ pageSize: 5 }}
                    scroll={{ x: 'max-content' }}
                    size="small"
                />
            ) : (
                <Empty description="No locations found" />
            )}
          </TabPane>
        </Tabs>

        {renderLocationForm()}
      </Card>
  );
};

export default CustomerLocationManagement;